import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getReviewData,
  approveDeliverable,
  requestRevision,
  ReviewPageData,
} from '../actions/review';

type Status = 'loading' | 'loaded' | 'error' | 'submitting' | 'success';

// YellowSlate Logo URL
const YELLOWSLATE_LOGO = 'https://cmsadmin.yellowslate.com/logo.png';

const PublicReview: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ReviewPageData | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [actionType, setActionType] = useState<'approve' | 'revision' | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    if (token) {
      loadReviewData();
    }
  }, [token]);

  const loadReviewData = async () => {
    if (!token) return;

    setStatus('loading');
    const result = await getReviewData(token);

    if (result.success && result.data) {
      setData(result.data);
      setStatus('loaded');
    } else {
      setErrorMessage(result.message || 'Failed to load review page');
      setStatus('error');
    }
  };

  const handleApprove = async () => {
    if (!token) {
      return;
    }

    setStatus('submitting');
    setActionType('approve');
    const result = await approveDeliverable(token);

    if (result.success) {
      setSuccessMessage(result.message || 'Approved successfully!');
      setStatus('success');
      // Reload data to show updated status
      await loadReviewData();
    } else {
      setErrorMessage(result.message || 'Failed to approve');
      setStatus('loaded');
    }
  };

  const handleRequestRevision = async () => {
    if (!token || !comment.trim()) {
      return;
    }

    setStatus('submitting');
    setActionType('revision');
    const result = await requestRevision(token, comment.trim());

    if (result.success) {
      setSuccessMessage(result.message || 'Revision request submitted!');
      setStatus('success');
      // Reload data to show updated status
      await loadReviewData();
      setComment('');
    } else {
      setErrorMessage(result.message || 'Failed to submit revision request');
      setStatus('loaded');
    }
  };

  // Render loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img src={YELLOWSLATE_LOGO} alt="YellowSlate" className="h-10 mx-auto mb-6" />
          <div className="w-16 h-16 border-4 border-gray-200 border-t-yellow-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading review...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (status === 'error' || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <img src={YELLOWSLATE_LOGO} alt="YellowSlate" className="h-8 mx-auto mb-6" />
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Review Not Found</h1>
          <p className="text-gray-600 mb-4">{errorMessage || 'This review link is invalid or does not exist.'}</p>
          <p className="text-sm text-gray-500">Please contact your account manager for a new review link.</p>
        </div>
      </div>
    );
  }

  const { deliverable, reviewLink, contact, school, blogContent, comments, alreadyReviewed } = data;

  // Get status badge color - use reviewLink status (per-contact)
  const getStatusBadge = () => {
    switch (reviewLink.status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Approved
          </span>
        );
      case 'needs_revision':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Revision Requested
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Pending Review
          </span>
        );
    }
  };

  // Get deliverable type icon
  const getTypeIcon = () => {
    switch (deliverable.type) {
      case 'video':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* YellowSlate Logo */}
              <img
                src={YELLOWSLATE_LOGO}
                alt="YellowSlate"
                className="h-8"
              />
              {school?.slate_name && (
                <>
                  <div className="h-6 w-px bg-gray-300"></div>
                  <div>
                    <h1 className="text-lg font-semibold text-gray-900">
                      {school.slate_name}
                    </h1>
                    {school?.city_name && school?.area_name && (
                      <p className="text-sm text-gray-500">
                        {school.area_name}, {school.city_name}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
            {getStatusBadge()}
          </div>
          {/* Contact Greeting */}
          {contact?.name && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-gray-600">
                Hello <span className="font-medium text-gray-900">{contact.name}</span>
                {contact.designation && (
                  <span className="text-gray-500"> ({contact.designation})</span>
                )}
                , please review the content below.
              </p>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8 flex-1">
        {/* Success Message */}
        {status === 'success' && successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-green-800 font-medium">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* LEFT COLUMN - Content Preview */}
          <div className="flex-1 lg:max-w-[65%]">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6">
                {/* Blog Content - Show HTML directly */}
                {deliverable.type === 'blog' && blogContent ? (
                  <div className="prose prose-sm sm:prose lg:prose-lg max-w-none">
                    {blogContent.featuredImage && (
                      <img
                        src={blogContent.featuredImage}
                        alt={blogContent.title}
                        className="w-full h-72 object-cover rounded-lg mb-6"
                      />
                    )}
                    {blogContent.title && (
                      <h1 className="text-2xl font-bold text-gray-900 mb-4">{blogContent.title}</h1>
                    )}
                    <div
                      dangerouslySetInnerHTML={{ __html: blogContent.content }}
                      className="blog-content"
                    />
                  </div>
                ) : deliverable.type === 'blog' ? (
                  <div className="text-center py-16 text-gray-500">
                    <svg className="w-20 h-20 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-xl font-medium mb-2">Blog content is being prepared</p>
                    <p className="text-sm">The content will be available here once it's ready for review.</p>
                  </div>
                ) : null}

                {/* Video Content */}
                {deliverable.type === 'video' && deliverable.url && (
                  <div className="py-4">
                    {deliverable.url.includes('youtube.com') || deliverable.url.includes('youtu.be') ? (
                      <div className="aspect-video w-full">
                        <iframe
                          src={deliverable.url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                          className="w-full h-full rounded-lg"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-600 mb-4">View the video at:</p>
                        <a
                          href={deliverable.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Watch Video
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* No URL and no content */}
                {deliverable.type !== 'blog' && !deliverable.url && (
                  <div className="text-center py-16 text-gray-500">
                    <svg className="w-20 h-20 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-xl font-medium mb-2">Content is being prepared</p>
                    <p className="text-sm">The content will be available here once it's ready for review.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Details & Actions (Sticky) */}
          <div className="lg:w-[35%]">
            <div className="lg:sticky lg:top-6 space-y-6">
              {/* Deliverable Info Card */}
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6">
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="p-2 bg-yellow-100 rounded-lg text-yellow-600">
                      {getTypeIcon()}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-gray-900">{deliverable.title}</h2>
                      <p className="text-sm text-gray-500 capitalize">{deliverable.type}</p>
                    </div>
                  </div>

                  {deliverable.description && (
                    <p className="text-gray-600 text-sm mb-4">{deliverable.description}</p>
                  )}

                  {/* Delivery Period */}
                  {deliverable.delivery_month && deliverable.delivery_year && (
                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {new Date(deliverable.delivery_year, deliverable.delivery_month - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                    </div>
                  )}

                  {/* Already reviewed banner - per-contact status */}
                  {alreadyReviewed && (
                    <div className={`p-3 rounded-lg ${
                      reviewLink.status === 'approved' ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'
                    }`}>
                      <p className={`text-sm ${
                        reviewLink.status === 'approved' ? 'text-green-800' : 'text-orange-800'
                      }`}>
                        <span className="font-medium">
                          {reviewLink.status === 'approved' ? '✓ Approved' : '⟳ Revision Requested'}
                        </span>
                        {reviewLink.reviewed_at && (
                          <span className="block mt-1 text-xs opacity-75">
                            {new Date(reviewLink.reviewed_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Review Actions - Hide if already approved */}
              {reviewLink.status !== 'approved' && (
                <div className="bg-white rounded-lg shadow-sm border">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Your Feedback
                    </h3>

                    {/* Show different UI based on current status */}
                    {reviewLink.status === 'needs_revision' ? (
                      // If already requested revision, show option to approve after changes
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                          You previously requested changes. If the content has been updated, you can now approve it.
                        </p>
                        <button
                          onClick={handleApprove}
                          disabled={status === 'submitting'}
                          className="w-full flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {status === 'submitting' && actionType === 'approve' ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              Approving...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Approve Now
                            </>
                          )}
                        </button>

                        {/* Option to add more feedback */}
                        <div className="pt-4 border-t">
                          <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                            Additional feedback (optional)
                          </label>
                          <textarea
                            id="comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Add more details if needed..."
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-sm"
                          />
                          {comment.trim() && (
                            <button
                              onClick={handleRequestRevision}
                              disabled={status === 'submitting'}
                              className="mt-2 w-full flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                            >
                              {status === 'submitting' && actionType === 'revision' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                  Submitting...
                                </>
                              ) : (
                                'Submit Additional Feedback'
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      // Pending status - show both options
                      <>
                        <div className="space-y-3">
                          <button
                            onClick={handleApprove}
                            disabled={status === 'submitting'}
                            className="w-full flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {status === 'submitting' && actionType === 'approve' ? (
                              <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                Approving...
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Approve
                              </>
                            )}
                          </button>

                          <button
                            onClick={() => {
                              const revisionSection = document.getElementById('revision-section');
                              if (revisionSection) {
                                revisionSection.classList.toggle('hidden');
                              }
                            }}
                            disabled={status === 'submitting'}
                            className="w-full flex items-center justify-center px-6 py-3 bg-white text-orange-600 border-2 border-orange-600 rounded-lg font-medium hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Request Changes
                          </button>
                        </div>

                        {/* Revision Comment Section */}
                        <div id="revision-section" className="hidden mt-4 pt-4 border-t">
                          <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                            What needs to be changed? <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Please describe what changes are needed..."
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-sm"
                          />
                          <button
                            onClick={handleRequestRevision}
                            disabled={!comment.trim() || status === 'submitting'}
                            className="mt-3 w-full flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                          >
                            {status === 'submitting' && actionType === 'revision' ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                Submitting...
                              </>
                            ) : (
                              'Submit Request'
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Thank you message for approved */}
              {reviewLink.status === 'approved' && (
                <div className="bg-green-50 rounded-lg border border-green-200 p-6 text-center">
                  <svg className="w-12 h-12 text-green-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-green-800 mb-1">Thank You!</h3>
                  <p className="text-sm text-green-700">You have approved this deliverable.</p>
                </div>
              )}

              {/* Previous Comments */}
              {comments.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border">
                  <div className="p-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Previous Comments</h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {comments.map((c) => (
                        <div key={c.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-900 text-sm">{c.user_name}</span>
                            <span className="text-xs text-gray-500">
                              {new Date(c.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                              })}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm">{c.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-2">
            <span className="text-sm text-gray-500">Powered by</span>
            <img src={YELLOWSLATE_LOGO} alt="YellowSlate" className="h-5" />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicReview;
